import React, { useState, useEffect } from "react";
import { Plus, Trash2, Save, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../services/api";
import { Card, Label, Input, Button } from "../components/ui";

export default function NovaOS() {
  const navigate = useNavigate();

  const [clientesAPI, setClientesAPI] = useState([]);
  const [tecnicosDB, setTecnicosDB] = useState([]);
  const [servicosDB, setServicosDB] = useState([]);

  const [cliente, setCliente] = useState("");
  const [tecnico, setTecnico] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [dataEncerramento, setDataEncerramento] = useState("");
  const [listaServicos, setListaServicos] = useState([]);
  const [listaProdutos, setListaProdutos] = useState([]);

  const [selServico, setSelServico] = useState({
    descricao: "",
    preco_unitario: "",
    quantidade: 1,
  });

  const [codigoBusca, setCodigoBusca] = useState("");
  const [buscandoProduto, setBuscandoProduto] = useState(false);
  const [selProduto, setSelProduto] = useState({
    descricao: "",
    preco_unitario: "",
    quantidade: 1,
  });

  const [showClientes, setShowClientes] = useState(false);
  const [showServicos, setShowServicos] = useState(false);
  const [showTecnicos, setShowTecnicos] = useState(false);

  useEffect(() => {
    api
      .get("/tecnicos")
      .then((res) => setTecnicosDB(res.data))
      .catch(() => {});
    api
      .get("/servicos")
      .then((res) => setServicosDB(res.data))
      .catch(() => {});

    api
      .get("/clientes")
      .then((res) => {
        if (Array.isArray(res.data)) {
          setClientesAPI(
            res.data.map((c) => ({
              id: c.ID,
              nome: c.DESCRICAO || c.NOME_FANTASIA || "Sem Nome",
            })),
          );
        }
      })
      .catch((err) => console.error("Erro ao buscar clientes:", err));
  }, []);

  const total = [...listaServicos, ...listaProdutos].reduce(
    (acc, item) => acc + item.preco_unitario * item.quantidade,
    0,
  );

  const buscarProdutoAPI = async () => {
    if (!codigoBusca) return;
    setBuscandoProduto(true);
    try {
      const res = await api.get(`/produtos/${codigoBusca}`);
      const prod = res.data;
      setSelProduto({
        descricao: prod.descricao || prod.NOME || "Sem descrição",
        preco_unitario: parseFloat(prod.preco_venda || prod.PRECO || 0),
        quantidade: 1,
      });
    } catch (e) {
      toast.error("Produto não encontrado ou erro na API.");
      setSelProduto({ descricao: "", preco_unitario: "", quantidade: 1 });
    } finally {
      setBuscandoProduto(false);
    }
  };

  const addServico = () => {
    if (selServico.descricao) {
      const precoTratado =
        parseFloat(selServico.preco_unitario.toString().replace(",", ".")) || 0;
      const qtdTratada = parseInt(selServico.quantidade) || 1;

      setListaServicos([
        ...listaServicos,
        { ...selServico, preco_unitario: precoTratado, quantidade: qtdTratada },
      ]);
      setSelServico({ descricao: "", preco_unitario: "", quantidade: 1 });
    }
  };

  const addProduto = () => {
    if (selProduto.descricao) {
      const precoTratado =
        parseFloat(selProduto.preco_unitario.toString().replace(",", ".")) || 0;
      const qtdTratada = parseInt(selProduto.quantidade) || 1;

      setListaProdutos([
        ...listaProdutos,
        { ...selProduto, preco_unitario: precoTratado, quantidade: qtdTratada },
      ]);
      setSelProduto({ descricao: "", preco_unitario: "", quantidade: 1 });
      setCodigoBusca("");
    }
  };

  const removerServico = (index) => {
    setListaServicos(listaServicos.filter((_, i) => i !== index));
  };

  const removerProduto = (index) => {
    setListaProdutos(listaProdutos.filter((_, i) => i !== index));
  };

  const salvarOS = async () => {
    if (!cliente || !tecnico) {
      toast.error("Preencha o cliente e o técnico!");
      return;
    }

    if (listaServicos.length === 0 && listaProdutos.length === 0) {
      toast.error(
        "A Ordem de Serviço deve conter pelo menos um serviço ou produto!",
      );
      return;
    }

    const dataEncerramentoFormatada = dataEncerramento
      ? dataEncerramento.split("-").reverse().join("/")
      : null;

    const usuarioLogado = JSON.parse(localStorage.getItem("@up2_user"));

    const payload = {
      cliente_nome: cliente,
      tecnico,
      criado_por: usuarioLogado?.nome || "Sistema",
      servicos: listaServicos,
      produtos: listaProdutos,
      observacoes,
      data_abertura: new Date().toLocaleDateString("pt-BR"),
      data_encerramento: dataEncerramentoFormatada,
      valor_total: total,
    };

    const loadingToast = toast.loading("Salvando OS...");
    try {
      await api.post("/os", payload);
      toast.success("Ordem de Serviço salva com sucesso!", {
        id: loadingToast,
      });
      navigate("/");
    } catch (e) {
      toast.error("Erro ao salvar OS no banco de dados.", { id: loadingToast });
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in pb-20">
      <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-end gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            Criar Nova OS
          </h2>
          <p className="text-slate-500">
            Gere ordens de serviço com clientes e produtos integrados ao MV.
          </p>
        </div>
        <Button
          onClick={salvarOS}
          variant="success"
          className="w-full sm:w-auto"
        >
          <Save size={18} className="mr-2" /> Salvar OS
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4">Dados Principais</h3>
          <div className="space-y-4">
            <div className="relative">
              <Label>Cliente</Label>
              <Input
                placeholder="Selecione ou digite o nome do cliente..."
                value={cliente}
                onChange={(e) => {
                  setCliente(e.target.value);
                  setShowClientes(true);
                }}
                onFocus={() => setShowClientes(true)}
                onBlur={() => setTimeout(() => setShowClientes(false), 200)}
              />
              {showClientes && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {clientesAPI
                    .filter((c) =>
                      c.nome.toLowerCase().includes(cliente.toLowerCase()),
                    )
                    .slice(0, 50)
                    .map((c, i) => (
                      <div
                        key={i}
                        className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700"
                        onClick={() => {
                          setCliente(c.nome);
                          setShowClientes(false);
                        }}
                      >
                        {c.nome}
                      </div>
                    ))}
                  {clientesAPI.filter((c) =>
                    c.nome.toLowerCase().includes(cliente.toLowerCase()),
                  ).length === 0 && (
                    <div className="px-3 py-2 text-sm text-slate-400">
                      Nenhum cliente encontrado...
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="relative">
              <Label>Técnico Responsável</Label>
              <Input
                placeholder="Selecione um técnico..."
                value={tecnico}
                onChange={(e) => {
                  setTecnico(e.target.value);
                  setShowTecnicos(true);
                }}
                onFocus={() => setShowTecnicos(true)}
                onBlur={() => setTimeout(() => setShowTecnicos(false), 200)}
              />
              {showTecnicos && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {tecnicosDB
                    .filter((t) =>
                      t.nome.toLowerCase().includes(tecnico.toLowerCase()),
                    )
                    .map((t) => (
                      <div
                        key={t.id}
                        className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700"
                        onClick={() => {
                          setTecnico(t.nome);
                          setShowTecnicos(false);
                        }}
                      >
                        {t.nome}
                      </div>
                    ))}
                  {tecnicosDB.filter((t) =>
                    t.nome.toLowerCase().includes(tecnico.toLowerCase()),
                  ).length === 0 && (
                    <div className="px-3 py-2 text-sm text-slate-400">
                      Nenhum técnico cadastrado...
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>Data de Encerramento (Opcional)</Label>
              <Input
                type="date"
                value={dataEncerramento}
                onChange={(e) => setDataEncerramento(e.target.value)}
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4">
            Observações Gerais (Opcional)
          </h3>
          <textarea
            className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 min-h-[190px] resize-none"
            placeholder="Descreva problemas relatados e outros detalhes..."
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
          ></textarea>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4">
          Itens da Ordem de Serviço
        </h3>

        <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-end bg-slate-50 p-4 rounded-lg border border-slate-200">
          <div className="relative w-full sm:flex-1">
            <Label>Serviço</Label>
            <Input
              placeholder="Selecione ou digite o nome do serviço..."
              value={selServico.descricao}
              onChange={(e) => {
                setSelServico({ ...selServico, descricao: e.target.value });
                setShowServicos(true);
              }}
              onFocus={() => setShowServicos(true)}
              onBlur={() => setTimeout(() => setShowServicos(false), 200)}
            />
            {showServicos && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {servicosDB
                  .filter((s) =>
                    s.descricao
                      .toLowerCase()
                      .includes(selServico.descricao.toLowerCase()),
                  )
                  .map((s, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700"
                      onClick={() => {
                        setSelServico({
                          ...selServico,
                          descricao: s.descricao,
                          preco_unitario: s.preco_padrao,
                        });
                        setShowServicos(false);
                      }}
                    >
                      {s.descricao}
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div className="w-full sm:w-24">
            <Label>Qtd</Label>
            <Input
              type="number"
              min="1"
              value={selServico.quantidade}
              onChange={(e) =>
                setSelServico({
                  ...selServico,
                  quantidade: e.target.value,
                })
              }
            />
          </div>
          <div className="w-full sm:w-32">
            <Label>Preço (R$)</Label>
            <Input
              type="text"
              placeholder="0,00"
              value={selServico.preco_unitario}
              onChange={(e) =>
                setSelServico({ ...selServico, preco_unitario: e.target.value })
              }
            />
          </div>
          <Button onClick={addServico} className="w-full sm:w-auto">
            <Plus size={18} className="sm:mr-0 mr-2" />
            <span className="sm:hidden">Adicionar Serviço</span>
          </Button>
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-4 items-start md:items-end bg-slate-50 p-4 rounded-lg border border-slate-200">
          <div className="w-full md:w-48">
            <Label>Código do Produto</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Ex: 123"
                value={codigoBusca}
                onChange={(e) => setCodigoBusca(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buscarProdutoAPI()}
              />
              <Button
                variant="outline"
                onClick={buscarProdutoAPI}
                disabled={buscandoProduto}
              >
                <Search size={18} />
              </Button>
            </div>
          </div>
          <div className="w-full md:flex-1">
            <Label>Descrição</Label>
            <Input
              placeholder="Busque pelo código ao lado ou digite o nome do produto..."
              value={selProduto.descricao}
              onChange={(e) =>
                setSelProduto({ ...selProduto, descricao: e.target.value })
              }
            />
          </div>
          <div className="w-full md:w-20">
            <Label>Qtd</Label>
            <Input
              type="number"
              min="1"
              value={selProduto.quantidade}
              onChange={(e) =>
                setSelProduto({
                  ...selProduto,
                  quantidade: e.target.value,
                })
              }
            />
          </div>
          <div className="w-full md:w-32">
            <Label>Preço (R$)</Label>
            <Input
              type="text"
              placeholder="0,00"
              value={selProduto.preco_unitario}
              onChange={(e) =>
                setSelProduto({ ...selProduto, preco_unitario: e.target.value })
              }
            />
          </div>
          <Button
            onClick={addProduto}
            className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus size={18} className="sm:mr-0 mr-2" />
            <span className="md:hidden">Adicionar Produto</span>
          </Button>
        </div>

        <div className="mt-6 border-t border-slate-200 pt-4">
          {listaServicos.map((item, idx) => (
            <div
              key={`srv-${idx}`}
              className="flex flex-col sm:flex-row justify-between p-3 border-b text-sm sm:items-center gap-2"
            >
              <div>
                <span className="font-bold text-slate-700 mr-2">[Serviço]</span>
                <span>
                  {item.quantidade}x {item.descricao}
                </span>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto">
                <span>
                  R${" "}
                  {(item.preco_unitario * item.quantidade)
                    .toFixed(2)
                    .replace(".", ",")}
                </span>
                <button
                  onClick={() => removerServico(idx)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}

          {listaProdutos.map((item, idx) => (
            <div
              key={`prd-${idx}`}
              className="flex flex-col sm:flex-row justify-between p-3 border-b text-sm sm:items-center gap-2"
            >
              <div>
                <span className="font-bold text-slate-700 mr-2">[Produto]</span>
                <span>
                  {item.quantidade}x {item.descricao}
                </span>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto">
                <span>
                  R${" "}
                  {(item.preco_unitario * item.quantidade)
                    .toFixed(2)
                    .replace(".", ",")}
                </span>
                <button
                  onClick={() => removerProduto(idx)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex justify-end pt-4">
        <div className="text-right">
          <p className="text-sm text-slate-500">Valor Total</p>
          <p className="text-4xl font-bold text-slate-900">
            R$ {total.toFixed(2).replace(".", ",")}
          </p>
        </div>
      </div>
    </div>
  );
}
